import { getIntlLocale } from "@/i18n";

/** Format integer cents as currency. Defaults to EUR. */
export function formatMoney(cents: number, currency = "EUR"): string {
  return new Intl.NumberFormat(getIntlLocale(), {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(cents / 100);
}

/** Format cents, or emptyLabel when zero (e.g. "—"). */
export function formatMoneyOrEmpty(
  cents: number,
  currency: string,
  emptyLabel: string,
): string {
  if (cents === 0) return emptyLabel;
  return formatMoney(cents, currency);
}

/**
 * Format cents for a money input field: UI-locale decimal separator, two
 * decimals, no grouping (e.g. ``1,50`` in Italian, ``1.50`` in English), so the
 * value round-trips through `parseMoneyInputToCents`.
 */
export function formatMoneyInput(cents: number): string {
  return new Intl.NumberFormat(getIntlLocale(), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: false,
  }).format(cents / 100);
}

/** Non-negative amount with at most two decimals, dot separator, no grouping. */
const NORMALIZED_AMOUNT = /^(\d+)(?:\.(\d{1,2}))?$/;

/**
 * Parse a free-form euro amount (IT/EU or US separators) into integer cents.
 * Accepts e.g. ``1234,56``, ``1.234,56``, ``1234.56``, ``1,234.56``, ``1.234``.
 * Returns `null` for anything else: signs, letters or trailing garbage
 * (``1,50abc``), more than two decimals, or several decimal separators.
 */
export function parseMoneyInputToCents(raw: string): number | null {
  let normalized = raw.trim().replace(/\u00A0/g, "").replace(/\s/g, "");
  if (!normalized) return null;

  const lastComma = normalized.lastIndexOf(",");
  const lastDot = normalized.lastIndexOf(".");

  if (lastComma !== -1 && lastDot !== -1) {
    normalized =
      lastComma > lastDot
        ? normalized.replace(/\./g, "").replace(",", ".")
        : normalized.replace(/,/g, "");
  } else if (lastComma !== -1) {
    normalized = normalized.replace(",", ".");
  } else if (lastDot !== -1) {
    const fraction = normalized.slice(lastDot + 1);
    if (fraction.length === 3 && /^\d+$/.test(fraction)) {
      normalized = normalized.replace(/\./g, "");
    }
  }

  const match = NORMALIZED_AMOUNT.exec(normalized);
  if (!match) return null;
  const [, euros, fraction = ""] = match;
  const cents = Number(euros) * 100 + Number(fraction.padEnd(2, "0"));
  return Number.isSafeInteger(cents) ? cents : null;
}
